<script lang="ts">
    // "Sign In" form.
    import { onDestroy, onMount } from 'svelte'
    import { writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import * as yup from 'yup'

    import { gettext as _ } from 'hydria/services/gettext'
    import { lg } from 'hydria/services/language'

    // Clear the error after some time
    const CLEAR_ERROR_AFTER_MS = 7500

    // Bunch of functions to call on destroy, like store subscriptions.
    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    interface Props {
        goto: (loc: string) => void
    }

    let { goto }: Props = $props()

    const schema = yup.object().shape({
        email: yup.string().email().required().label('Email'),
    })

    interface IFields {
        email: string
    }
    const initialFields: IFields = { email: '' }
    const fields: IFields = $state({ ...initialFields })

    // When true we are signing in, (e.g. show a spinner)
    let communicating = writable(false)
    let emailSent = writable(false)

    // When not null, there was an error to show.
    // That error is shown for a few seconds or until a value changes.
    const error = writable(null as null | string)
    let errorTimer: ReturnType<typeof setTimeout> | undefined = undefined

    function clearErrorTimer() {
        if (errorTimer) {
            clearTimeout(errorTimer)
            errorTimer = undefined
        }
    }
    function clearError() {
        if ($error != null) {
            clearErrorTimer()
            error.set(null)
        }
    }

    unsubs.push(
        error.subscribe((newError) => {
            // This is called when the `error` store changes
            clearErrorTimer()
            if (newError != null) {
                errorTimer = setTimeout(() => {
                    errorTimer = undefined
                    clearError()
                }, CLEAR_ERROR_AFTER_MS)
            }
        }),
    )

    // Set 'isvalid' variables based on their validity when a value changes
    // @ts-expect-error
    let emailIsValid = $derived(schema.fields.email.isValidSync(fields.email))
    let formIsValid = $derived(schema.isValidSync(fields))

    $effect(() => {
        fields
        if (errorTimer != null) clearError()
    })

    async function reqPassword(email: string) {
        let fdata = new Blob([JSON.stringify({ email: email }, null, 0)], { type: 'application/json' })
        const headers = new Headers()
        headers.append('Accept', 'application/json')
        headers.append('X-Lg', $lg)

        const r = await fetch(new Request('/json/session/1/password/reset'), {
            method: 'POST',
            headers: headers,
            body: fdata,
        })

        let data: any
        try {
            data = await r.json()
        } catch {}

        if (r.status != 200) {
            throw { success: false, error: data && data.error ? data.error : null, ecode: data && data.ecode ? data.ecode : null }
        }

        if (data.success && data.ecode == 'EMAIL_SENT') {
            return { success: true }
        }
        throw { success: false, error: data && data.error ? data.error : null, ecode: data && data.ecode ? data.ecode : null }
    }

    // Send an email
    async function formSubmit() {
        if (!formIsValid) {
            return
        }
        error.set(null)
        communicating.set(true)
        let res
        try {
            res = await reqPassword(fields.email)
        } catch (e) {
            res = e
        }
        communicating.set(false)
        if (!res.success) {
            if (res.error) error.set(res.error)
            else error.set($_('L’opération n’a pas pu être effectuée'))
        } else {
            emailSent.set(true)
        }
    }

    let mainInput: HTMLElement | undefined = $state()
    onMount(() => {
        if (mainInput) mainInput.focus()
    })
</script>

<div class="section-title">
    {$_('Mot de passe oublié ?')}
</div>

<form
    novalidate
    onsubmit={(e) => {
        e.preventDefault()
        formSubmit()
    }}
>
    {#if $emailSent}
        <p>
            {#if $lg == 'fr'}
                Si un compte existe avec l’adresse {fields.email}, vous recevrez un courriel contenant un lien permettant d’appliquer un nouveau mot de passe à votre compte.
            {:else}
                If an account exists with the email address {fields.email}, you will receive an email containing a link to a page allowing you to set a new password.
            {/if}
        </p>
        {#if $lg == 'fr'}
            <p>Le courriel devrait arriver sous peu. Assurez-vous aussi de vérifier dans votre boîte de messages indésirables.</p>
        {:else}
            <p>The email should arrive shortly. Also be sure to check your spam box.</p>
        {/if}
    {:else}
        <div class="inp-email">
            <label for="email" class="sr-only">{$_('Adresse électronique')}</label>
            <div class="input-group" class:is-ok={emailIsValid} class:disabled={$communicating}>
                <span class="input-group-text"><Icon inline icon="fa:user" /></span>
                <input class="form-control" id="email" type="email" bind:value={fields.email} disabled={$communicating} bind:this={mainInput} placeholder={$_('Adresse électronique')} oninput={() => clearError()} class:is-invalid={!emailIsValid} />
            </div>
        </div>

        {#if $error != null}
            <div class="error">
                <div class="alert alert-danger">{$error}</div>
            </div>
        {:else}
            <div class="inp-action">
                <button class="btn btn-primary btn-block" type="submit" disabled={!formIsValid || $communicating || $error != null}>
                    {#if $communicating}{$_('Veuillez patienter…')}
                    {:else}{$_('Changer le mot de passe')}
                    {/if}
                </button>
            </div>
        {/if}
    {/if}

    {#if !$communicating}
        <div class="inp-action">
            <button class="btn btn-outline-secondary btn-block" type="button" onclick={() => goto('signin')}>{$_('Se connecter')}</button>
        </div>
    {/if}
</form>
